// Port of companion/src/script/decompile.mjs at tag js-core-freeze-2026-09-05 (HEAD cb2d250),
// extended by Round 1, lane A (docs/briefs/ROUND1-SPEC-2026-09-05.md sections A.2, B, C, D.2, E).
// Everything the spec does not change writes exactly the frozen bytes: the same script text, the
// same names table (Maps in the same insertion order). Spellings that differ for the editor-client
// lint profile and nothing else: `for...of` is forEach or an index loop, `Array.prototype.at(-1)` is
// an index, and `targets.every(Boolean)` is a type-guarding filter of the same length.
//
// Round 1 changes, each marked `// Round 1` at the spot: the sidecar map is version 3 (kind per step
// type, stepTypeId, sig always present; key order kind, branchId, stepId, stepTypeId, answerIds, sig,
// line); every text line and inline grammar position is escaped (B.1); a choice writes its speaker
// above the prompt (D.2); types 8/9/10 write their tag (C); a type-5 step is `> end: <text>`; types
// 6, 7, 13, 14, 15 have a spelling; a check the scalar grammar cannot spell is a `> check:` envelope
// (E.4); an increase/decrease/view action writes its verb (E.5); the displayed scene heading is the
// node's description when it has one, else its title (F.2); signatures per A.2.
import type {
  Block,
  Branch,
  Decompiled,
  DecompileOptions,
  EngineId,
  Fixture,
  NameDomain,
  NamesTable,
  NameWords,
  RawRoll,
  ScriptRoll,
  SidecarBlock3,
  SidecarKind3,
  SidecarMapVersion3,
  Step,
  StepAction,
  StepCheck,
  SuppliedNames,
  SwitchArm,
} from '../../../dorian-shared/types/episodeScript';
import { escapeInline, escapeTextLine } from './escape';

const ARROW = '→';

// Bumped whenever the sidecar map's shape changes. A map written by an older
// version is stale even when the script text still matches, because the fields
// the compiler aligns on would be missing.
// Round 1 (3.1): version 3 — kind per step type, stepTypeId, sig always present.
export const MAP_VERSION: SidecarMapVersion3 = 3;

/** A sidecar entry before its line is known: what `record` receives (decompile.mjs:203-206). */
type SidecarEntry = Omit<SidecarBlock3, 'line'>;

/** The identity every step block carries (decompile.mjs:218); `kind` is added per step type. */
type StepIdentity = Omit<SidecarBlock3, 'kind' | 'line'>;

// Round 1 (E.1): the six operators the `> if:` grammar spells; everything else is an envelope.
const SCALAR_OPERATORS = new Set(['equal', 'notEqual', 'greater', 'atLeast', 'less', 'atMost']);

// decompile.mjs:8-11. A Map yields its entries; anything else goes through Object.entries, so an
// id → word object, an array (index → word) and undefined (nothing) all read the frozen way.
function entries(value: NameWords | undefined): [EngineId, string][] {
  if (value instanceof Map) return Array.from(value.entries());
  return Object.entries(value ?? {});
}

// decompile.mjs:13-19. byId lists the fixture's ids first (as `${prefix}${id}`), then the supplied
// words: a supplied id already present keeps its position, a new one is appended. byName folds
// each word to lower case; a later duplicate word wins, as Map.set does.
function domain(prefix: string, ids: Set<EngineId>, supplied: NameWords | undefined): NameDomain {
  const byId = new Map<string, string>(Array.from(ids).map((id): [string, string] => [String(id), `${prefix}${id}`]));
  entries(supplied).forEach(([id, name]) => {
    byId.set(String(id), String(name));
  });
  const byName = new Map<string, string>();
  byId.forEach((name, id) => {
    byName.set(name.toLocaleLowerCase(), id);
  });
  return { byId, byName };
}

export function createNamesTable(fixture: Fixture | null | undefined, supplied: SuppliedNames = {}): NamesTable {
  const characterIds = new Set<EngineId>();
  const expressionIds = new Set<EngineId>();
  const locationIds = new Set<EngineId>();
  const variableIds = new Set<EngineId>();
  (fixture?.branches ?? []).forEach((branch) => {
    if (branch.locationId != null) locationIds.add(branch.locationId);
    (branch.steps ?? []).forEach((step) => {
      if (step.characterId != null) characterIds.add(step.characterId);
      if (step.characterExpressionId != null) expressionIds.add(step.characterExpressionId);
      if (step.check?.variableId != null) variableIds.add(step.check.variableId);
      if (step.action?.variableId != null) variableIds.add(step.action.variableId);
      // Round 1 (C, type 15): a roll names a variable too.
      if (step.roll?.variableId != null) variableIds.add(step.roll.variableId);
    });
  });
  return {
    characters: domain('C', characterIds, supplied.characters),
    expressions: domain('E', expressionIds, supplied.expressions),
    locations: domain('L', locationIds, supplied.locations),
    variables: domain('V', variableIds, supplied.variables),
  };
}

// Round 1 (F.2): the displayed heading is the node's description when it has one, else its title
// (ep02's 189 descriptions are empty, so its headings are the frozen ones).
export function uniqueSceneNames(branches: Branch[]): Map<string, string> {
  const bases = branches.map((branch) => String(branch.description || branch.title || `Node ${branch.id}`));
  const counts = new Map<string, number>();
  bases.forEach((base) => {
    counts.set(base, (counts.get(base) ?? 0) + 1);
  });
  const used = new Set<string>();
  const names = new Map<string, string>();
  branches.forEach((branch, index) => {
    const base = bases[index];
    let name = (counts.get(base) ?? 0) > 1 ? `${base} ${branch.id}` : base;
    while (used.has(name)) name = `${name} ${branch.id}`;
    used.add(name);
    names.set(String(branch.id), name);
  });
  return names;
}

function named(table: NameDomain, id: EngineId | null | undefined, prefix: string): string | null {
  if (id == null) return null;
  return table.byId.get(String(id)) ?? `${prefix}${id}`;
}

// decompile.mjs:64-68. A string and any other JSON value both go through JSON.stringify; only
// undefined, which JSON.stringify has no spelling for, is written as null.
function scalar(value: unknown): string {
  if (value === undefined) return 'null';
  return JSON.stringify(value);
}

function priced(text: string | null | undefined, price: unknown): string {
  return `${text ?? ''}${Number(price) > 0 ? ` [${price}]` : ''}`;
}

function routed(text: string, targetId: EngineId | null | undefined, sceneNames: Map<string, string>): string {
  const target = targetId == null ? null : sceneNames.get(String(targetId));
  // Round 1 (B.1): a scene name is written as a route the way an arm is — arrows and backslashes escaped.
  return target ? `${text} ${ARROW} ${escapeInline(target, 'arm')}` : text;
}

function plain(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

// The engine writes "the variable is true" as set/equal 1. Where a step says
// exactly that and nothing else, the script says it the way a writer would.
// Round 1 (E.5): the same shape with 0 is `> forget:`.
function isBooleanSet(step: Step, flag: '1' | '0'): boolean {
  const action: StepAction = step.action ?? {};
  return plain(action.type) === 'set' && plain(action.value) === flag
    && Object.keys(action).every((key) => ['type', 'value', 'variableId'].includes(key))
    && (step.text == null || step.text === '')
    && step.characterId == null && step.characterExpressionId == null
    && !(step.answers ?? []).length && !(step.data ?? []).length;
}

function booleanArms(step: Step, sceneNames: Map<string, string>): string[] | null {
  const check: StepCheck = step.check ?? {};
  const arms = step.switch ?? [];
  if (plain(check.operator) !== 'equal' || plain(check.value) !== '1') return null;
  if (arms.length !== 2 || plain(arms[0].value) !== 'true' || plain(arms[1].value) !== 'false') return null;
  const targets = arms.map((arm) => (arm.gotoBranchId == null ? null : sceneNames.get(String(arm.gotoBranchId))));
  // Only when both arms name a scene: an arm with no destination would be read
  // back as a fall-through, which is a different graph.
  const found = targets.filter((target): target is string => Boolean(target));
  return found.length === targets.length ? found : null;
}

// Round 1 (E.4): a check the `> if:` grammar can spell — one of the scalar six, a scalar value (a
// memory link `{type:'variable'}` counts: E.3), no other check field, and arms that are nothing but
// `{ value, gotoBranchId }`. Everything else is written as the `> check:` envelope, verbatim.
function isScalarCheck(step: Step): boolean {
  const check: StepCheck = step.check ?? {};
  if (!SCALAR_OPERATORS.has(String(check.operator))) return false;
  if (check.value === undefined || Array.isArray(check.value)) return false;
  if (check.value !== null && typeof check.value === 'object' && (check.value as { type?: unknown }).type === 'variable[]') return false;
  if (Object.keys(check).some((key) => !['variableId', 'operator', 'value'].includes(key))) return false;
  return (step.switch ?? []).every((arm) => arm !== null && typeof arm === 'object' && 'value' in arm
    && Object.keys(arm).every((key) => key === 'value' || key === 'gotoBranchId'));
}

function envelopeOf(step: Step): string {
  return JSON.stringify({ check: step.check ?? {}, switch: step.switch ?? [] });
}

// A block's identity is what it says, not where it sits. `sig` is that identity:
// the kind of block plus the words in it, normalised so that indentation, case
// and line wrapping do not change it. The compiler aligns the map against a
// re-parsed script on this, which is why the two spellings below — one read off
// an engine step, one read off a parsed block — have to agree exactly.
function normalise(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

// U+0001, spelled by code point: the character itself is invisible and an editor may drop it.
const SEPARATOR = String.fromCharCode(1);

function signature(kind: string, ...parts: unknown[]): string {
  return [kind, ...parts.map(normalise)].join(SEPARATOR);
}

// Routing is deliberately absent from every signature. A choice that now sends
// the reader somewhere else is the same choice, re-routed — a change to report
// against the step's own id, not a different step.
export function sceneSignature(name: string): string {
  return signature('scene', name);
}

function variableSlot(id: unknown): string {
  return id == null ? '?' : String(id);
}

function hubOptionTitles(step: Step): (string | null | undefined)[] {
  return [...(step.hubOptions ?? [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((option) => option.title);
}

/** Round 1 (3.1): the sidecar kind of a step, by its engine type. */
export function sidecarKind(stepTypeId: number): SidecarKind3 {
  if (stepTypeId === 1 || stepTypeId === 8 || stepTypeId === 9 || stepTypeId === 10) return 'character';
  if (stepTypeId === 2) return 'narration';
  if (stepTypeId === 3) return 'choice';
  if (stepTypeId === 5) return 'ending';
  if (stepTypeId === 6 || stepTypeId === 7) return 'legacy';
  if (stepTypeId === 11) return 'check';
  if (stepTypeId === 12) return 'remember';
  if (stepTypeId === 13) return 'achieve';
  if (stepTypeId === 14) return 'video';
  if (stepTypeId === 15) return 'roll';
  if (stepTypeId === 16) return 'hub';
  // A type the engine does not have decompiles to its text as narration, so it signs as one.
  return 'narration';
}

// Round 1 (A.2): the video label the script shows — the title, or `video <id>` when it has none.
function videoLabel(step: Step): string {
  const title = plain(step.video?.title);
  return title || `video ${plain(step.video?.id)}`.trim();
}

export function stepSignature(step: Step): string {
  const kind = sidecarKind(step.stepTypeId);
  if (kind === 'character') {
    // Round 1: a speaking step whose speaker the script cannot name is written as its text alone (the
    // frozen `?` line read back as a margin note and lost the step), so it signs as that paragraph.
    if (step.characterId == null) return signature('narration', step.text);
    // A reaction line (type 10) is one line and carries no text, so its text is not in its identity.
    if (step.stepTypeId === 10) return signature('character', '');
    return signature('character', step.text);
  }
  if (step.stepTypeId === 3) {
    return signature('choice', step.text, ...(step.answers ?? []).map((answer) => answer.text));
  }
  if (step.stepTypeId === 11) {
    // Round 1 (A.2): the frozen string for a scalar check; the envelope signs on its JSON.
    if (!isScalarCheck(step)) return signature('directive', 'if', JSON.stringify(step.check ?? {}));
    const check: StepCheck = step.check ?? {};
    return signature('directive', 'if', variableSlot(check.variableId), check.operator ?? 'equal', check.value);
  }
  if (step.stepTypeId === 12) {
    const action: StepAction = step.action ?? {};
    return signature('directive', 'set', variableSlot(action.variableId), action.value);
  }
  if (step.stepTypeId === 16) {
    return signature(
      'hub',
      step.hubSettings?.header?.text,
      step.hubSettings?.subheader?.text,
      ...hubOptionTitles(step),
    );
  }
  // Round 1 (A.2): the new kinds.
  if (kind === 'ending') return signature('ending', step.text);
  if (kind === 'achieve') return signature('achieve', step.achievementId);
  if (kind === 'video') return signature('video', step.video?.id);
  if (kind === 'roll') return signature('roll', JSON.stringify(step.roll ?? null));
  if (kind === 'legacy') return signature('legacy', step.stepTypeId, step.text);
  // Every other step type decompiles to its text as narration, so it signs as one.
  return signature('narration', step.text);
}

// Round 1 (A.2): the roll a parsed block stands for, in the engine's key order, so that the block
// signs exactly as the step it came from.
function rollOf(block: { variableId: EngineId | null; roll: ScriptRoll }): RawRoll {
  if (block.roll.type === 'dice') {
    return { type: 'dice', variableId: block.variableId as EngineId, diceNotation: block.roll.diceNotation };
  }
  const roll: RawRoll = {
    type: block.roll.type, variableId: block.variableId as EngineId, min: block.roll.min as number, max: block.roll.max as number,
  };
  if (block.roll.modifier !== undefined) roll.modifier = block.roll.modifier;
  return roll;
}

// The same identity, read off a block the parser produced. Blocks that are not
// steps — margin notes, `> go to:` — have none.
// Round 1: a `> end` block signs as the ending step it stands for; a video block that shows the
// `video <id>` label signs on the id, a titled one on its title; an achieve block signs on its
// description (the id is not in the script: the compiler pairs it by similarity, A.3).
export function blockSignature(block: Block): string | null {
  if (block.type === 'character') return signature('character', block.text);
  if (block.type === 'narration') return signature('narration', block.text);
  if (block.type === 'choice') {
    return signature('choice', block.text, ...(block.answers ?? []).map((answer) => answer.text));
  }
  if (block.type === 'hub') {
    return signature('hub', block.header, block.subheader, ...(block.options ?? []).map((option) => option.text));
  }
  if (block.type === 'directive' && block.directive === 'if') {
    return signature('directive', 'if', variableSlot(block.variableId), block.operator ?? '?', block.value);
  }
  if (block.type === 'directive' && block.directive === 'check') {
    return signature('directive', 'if', JSON.stringify(block.payload.check));
  }
  if (block.type === 'directive' && (block.directive === 'remember' || block.directive === 'forget' || block.directive === 'view')) {
    return signature('directive', 'set', variableSlot(block.variableId), block.value);
  }
  if (block.type === 'directive' && block.directive === 'end') return signature('ending', block.text ?? '');
  if (block.type === 'video') {
    const bare = block.label.match(/^video (\S+)$/);
    return signature('video', bare ? bare[1] : block.label);
  }
  if (block.type === 'achieve') return signature('achieve', block.description);
  if (block.type === 'roll') return signature('roll', JSON.stringify(rollOf(block)));
  if (block.type === 'legacy') return signature('legacy', block.stepTypeId, block.text);
  return null;
}

// Round 1 (B.1): a step text as written lines, each escaped for its position.
function textLines(text: string | null | undefined): string[] {
  const raw = String(text ?? '').split('\n');
  return raw.map((line, index) => escapeTextLine(line, index === 0 ? 'first' : 'later', index + 1 < raw.length ? raw[index + 1] : null));
}

// Round 1 (C): a text that is written on the lines under a directive or a NAME line; an empty text
// is one escaped empty line, so the step still puts something on the page.
function bodyLines(text: string | null | undefined): string[] {
  return text == null || text === '' ? ['\\'] : textLines(text);
}

// Round 1 (E.5): the places a `> view:` action names, from the list the read carries (or a JSON
// string of one — UNKNOWN whether the engine ever stores that; spec L).
function viewPlaces(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((place) => String(place));
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((place) => String(place));
    } catch {
      return [value];
    }
  }
  return [plain(value)];
}

// Round 1 (C, type 15): the `> roll:` line of a step's roll.
function rollLine(variable: string, roll: RawRoll | undefined): string {
  if (!roll) return `> roll: ${variable}`;
  if (roll.type === 'dice') return `> roll: ${variable} = ${plain(roll.diceNotation)}`;
  let operator = '=';
  if (roll.type === 'increase') operator = '+=';
  if (roll.type === 'decrease') operator = '-=';
  const modifier = roll.modifier === undefined || roll.modifier === null
    ? ''
    : ` ${Number(roll.modifier) >= 0 ? '+' : ''}${roll.modifier}`;
  return `> roll: ${variable} ${operator} ${plain(roll.min)}..${plain(roll.max)}${modifier}`;
}

export function decompile(fixture: Fixture, options: DecompileOptions = {}): Decompiled {
  if (!fixture || !Array.isArray(fixture.branches)) {
    throw new TypeError("Basil couldn't open this episode. Try reloading it — nothing in it has changed.");
  }
  const wanted = options.branchIds;
  const selected = wanted?.length
    ? fixture.branches.filter((branch) => wanted.some((id) => String(id) === String(branch.id)))
    : fixture.branches;
  const names = createNamesTable(fixture, options.names);
  const sceneNames = uniqueSceneNames(fixture.branches);
  const lines: string[] = [];
  const blocks: SidecarBlock3[] = [];

  const blank = (): void => {
    if (lines.length && lines[lines.length - 1] !== '') lines.push('');
  };
  const add = (values: string[]): number => {
    const line = lines.length + 1;
    lines.push(...values);
    return line;
  };
  // A map entry is written only when the block put words on the page. An engine
  // step that decompiles to nothing but blank lines has nothing there for the
  // writer to touch, and an identity with no text would read back as a deleted
  // block on the very next compile. The line number is a hint for messages; the
  // compiler aligns on `sig`. (Round 1: every step now writes something, so every step has an entry.)
  const record = (values: string[], block: SidecarEntry): void => {
    const line = add(values);
    if (values.some((value) => String(value).trim() !== '')) blocks.push({ ...block, line });
  };
  // Round 1 (C, D.2): the `NAME (mood)[ tag]` line of a step that has a speaker.
  const nameLine = (step: Step, tag = ''): string => {
    const character = named(names.characters, step.characterId, 'C') ?? '?';
    const expression = named(names.expressions, step.characterExpressionId, 'E');
    return `${character.toLocaleUpperCase()}${expression ? ` (${expression})` : ''}${tag}`;
  };

  selected.forEach((branch) => {
    blank();
    const sceneName = sceneNames.get(String(branch.id)) as string;
    const location = named(names.locations, branch.locationId, 'L');
    record([`== ${escapeInline(sceneName, 'heading')}${location ? ` @ ${escapeInline(location, 'heading')}` : ''}`], {
      kind: 'scene',
      branchId: branch.id,
      stepId: null,
      stepTypeId: null,
      answerIds: [],
      sig: sceneSignature(sceneName),
    });

    (branch.steps ?? []).forEach((step) => {
      blank();
      const identity: StepIdentity = {
        branchId: branch.id, stepId: step.id, stepTypeId: step.stepTypeId, answerIds: [], sig: stepSignature(step),
      };
      const kind = sidecarKind(step.stepTypeId);
      // Round 1: the tag that spells a speaking step's type; a step with no character has no NAME
      // line at all (the frozen `?` placeholder read back as a margin note, which lost the step and
      // its text), so it is written as its text and signs as that text (stepSignature).
      const speaking = kind === 'character' && step.characterId != null;
      if (kind === 'character' && !speaking) {
        // With no speaker and no text there is nothing on the page to edit, so the step keeps no
        // line and no identity and the compiler leaves it alone — the frozen rule for a step that
        // wrote nothing but blanks.
        record(plain(step.text) === '' ? [] : bodyLines(step.text), { kind: 'character', ...identity });
      } else if (step.stepTypeId === 1) {
        record([nameLine(step), ...bodyLines(step.text)], { kind: 'character', ...identity });
      } else if (step.stepTypeId === 8) {
        record([nameLine(step, ' [texting]'), ...bodyLines(step.text)], { kind: 'character', ...identity });
      } else if (step.stepTypeId === 9) {
        record([nameLine(step, ' [thinking]'), ...bodyLines(step.text)], { kind: 'character', ...identity });
      } else if (step.stepTypeId === 10) {
        record([nameLine(step, ' [reaction]')], { kind: 'character', ...identity });
      } else if (step.stepTypeId === 2) {
        // A narration with no text writes nothing and keeps no identity, as it did frozen: there is
        // no line for the writer to touch and an identity with no text would read back as a deletion.
        record(plain(step.text) === '' ? [] : textLines(step.text), { kind: 'narration', ...identity });
      } else if (step.stepTypeId === 3) {
        const choiceLines: string[] = [];
        // Round 1 (D.2): the speaker above the prompt.
        if (step.characterId != null) choiceLines.push(nameLine(step));
        if (step.text != null && step.text !== '') {
          // The last prompt line is always followed by an answer line, so a NAME-shaped one is escaped.
          const prompt = String(step.text).split('\n');
          choiceLines.push(...prompt.map((line, index) => escapeTextLine(line, index === 0 ? 'first' : 'later', index + 1 < prompt.length ? prompt[index + 1] : '*')));
        }
        (step.answers ?? []).forEach((answer) => {
          choiceLines.push(routed(`* ${priced(escapeInline(plain(answer.text), 'answer'), answer.answerPrice)}`, answer.gotoBranchId, sceneNames));
        });
        record(choiceLines.length ? choiceLines : ['*'], {
          kind: 'choice',
          ...identity,
          // An engine answer always carries its id; the frozen code copies whatever is there.
          answerIds: (step.answers ?? []).map((answer) => answer.id as EngineId),
        });
      } else if (step.stepTypeId === 5) {
        // Round 1 (C): the ending step, its text on the directive line and the lines under it.
        const text = String(step.text ?? '').split('\n');
        // An empty first line is written as the escaped empty line `\` when more lines follow, so the
        // text reads back with the blank line it had; an ending whose whole text is empty is `> end`.
        let first = `: ${escapeInline(text[0], 'label')}`;
        if (text[0] === '') first = text.length > 1 ? ': \\' : '';
        const rest = text.slice(1).map((line, index) => escapeTextLine(line, 'later', index + 2 < text.length ? text[index + 2] : null));
        record([`> end${first}`, ...rest], { kind: 'ending', ...identity });
      } else if (step.stepTypeId === 6) {
        const subType = plain(step.subType);
        record([`> results${subType ? `: ${subType}` : ''}`, ...(plain(step.text) ? textLines(step.text) : [])], { kind: 'legacy', ...identity });
      } else if (step.stepTypeId === 7) {
        record(['> player select', ...(plain(step.text) ? textLines(step.text) : [])], { kind: 'legacy', ...identity });
      } else if (step.stepTypeId === 11) {
        if (!isScalarCheck(step)) {
          record([`> check: ${envelopeOf(step)}`], { kind: 'check', ...identity });
        } else {
          const check: StepCheck = step.check ?? {};
          const variable = named(names.variables, check.variableId, 'V') ?? '?';
          const human = booleanArms(step, sceneNames);
          const checkLines = human
            ? [`> if: ${variable}`, `> go to: ${escapeInline(human[0], 'arm')}`, '> else', `> go to: ${escapeInline(human[1], 'arm')}`]
            : [`> if: ${variable} ${check.operator ?? 'equal'} ${scalar(check.value)}`];
          if (!human) {
            (step.switch ?? []).forEach((arm: SwitchArm) => {
              checkLines.push(routed(`> ${escapeInline(scalar(arm.value), 'arm')}`, arm.gotoBranchId, sceneNames));
            });
          }
          record(checkLines, { kind: 'check', ...identity });
        }
      } else if (step.stepTypeId === 12) {
        const action: StepAction = step.action ?? {};
        const variable = named(names.variables, action.variableId, 'V') ?? '?';
        let text: string;
        if (plain(action.type) === 'increase') text = `> remember: ${variable} += ${scalar(action.value)}`;
        else if (plain(action.type) === 'decrease') text = `> remember: ${variable} -= ${scalar(action.value)}`;
        else if (plain(action.type) === 'view') text = `> view: ${variable} in ${viewPlaces(action.value).join(', ')}`;
        else if (isBooleanSet(step, '1')) text = `> remember: ${variable}`;
        else if (isBooleanSet(step, '0')) text = `> forget: ${variable}`;
        else text = `> remember: ${variable} = ${scalar(action.value)}`;
        record([text], { kind: 'remember', ...identity });
      } else if (step.stepTypeId === 13) {
        record([`> achieve: ${escapeInline(plain(step.achievementDescription), 'label')}`, ...(plain(step.text) ? textLines(step.text) : [])], { kind: 'achieve', ...identity });
      } else if (step.stepTypeId === 14) {
        record([`> video: ${escapeInline(videoLabel(step), 'label')}`, ...(plain(step.text) ? textLines(step.text) : [])], { kind: 'video', ...identity });
      } else if (step.stepTypeId === 15) {
        const variable = named(names.variables, step.roll?.variableId, 'V') ?? '?';
        // A random step with no roll to spell writes nothing, as a text-less step did in the frozen
        // decompiler: it keeps no line the writer could edit and no map entry, so the compiler leaves
        // it exactly as it found it rather than reading a line back that says less than the step.
        record(step.roll ? [rollLine(variable, step.roll)] : [], { kind: 'roll', ...identity });
      } else if (step.stepTypeId === 16) {
        const header = step.hubSettings?.header?.text ?? '';
        const subheader = step.hubSettings?.subheader?.text ?? '';
        const hubLines = [`> hub: ${escapeInline(header, 'hubHeader')} | ${escapeInline(subheader, 'hubHeader')}`];
        [...(step.hubOptions ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).forEach((option) => {
          hubLines.push(routed(`- ${priced(escapeInline(plain(option.title), 'option'), option.answerPrice ?? option.price)}`, option.gotoBranchId, sceneNames));
        });
        record(hubLines, {
          kind: 'hub',
          ...identity,
          // A hub option always carries its id; the frozen code copies whatever is there.
          answerIds: (step.hubOptions ?? []).map((option) => option.id as EngineId),
        });
      } else {
        record(bodyLines(step.text), { kind, ...identity });
      }
    });

    if (branch.gotoBranchId != null) {
      blank();
      const target = sceneNames.get(String(branch.gotoBranchId));
      // The node's route, not a step: it carries no step id and no signature.
      record([`> go to: ${escapeInline(target ?? `Node ${branch.gotoBranchId}`, 'arm')}`], {
        kind: 'route',
        branchId: branch.id,
        stepId: null,
        stepTypeId: null,
        answerIds: [],
        sig: null,
      });
    }
  });

  while (lines[lines.length - 1] === '') lines.pop();
  return { text: `${lines.join('\n')}\n`, map: { version: MAP_VERSION, blocks }, names };
}
